import { computed, ref } from 'vue'

import NotificationsService from '@/services/notifications'
import {
  getFcmToken,
  getLastRegisteredToken,
  getWebPushSupportStatus,
  readWebPushOptIn,
  setLastRegisteredToken,
  subscribeFcmWebPush,
  writeWebPushOptIn,
  type WebPushSupportStatus,
} from '@/utils/webPush'

/**
 * If notification permission is already granted, send the current FCM token to
 * Novu when it differs from the last one we stored. Does not request permission.
 * Returns true when a token is present and registered (or already was).
 */
export async function syncGrantedWebPushToken(): Promise<boolean> {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return false
  }

  const currentToken = await getFcmToken()
  if (!currentToken) {
    return false
  }

  const lastToken = getLastRegisteredToken()
  if (currentToken === lastToken) {
    writeWebPushOptIn(true)
    return true
  }

  try {
    await NotificationsService.registerWebPushDevice({ deviceToken: currentToken })
    setLastRegisteredToken(currentToken)
    writeWebPushOptIn(true)
    return true
  } catch (err) {
    console.warn('[FCM] Failed to register token during refresh:', err)
    return false
  }
}

/**
 * Opt-in Web Push for Novu. Call enable() only from a user gesture (button).
 * No aggressive permission popup on load.
 */
export function useNovuWebPush() {
  const supportStatus = ref<WebPushSupportStatus>('unsupported')
  const permission = ref<NotificationPermission | 'unsupported'>(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
  )
  const optedIn = ref(readWebPushOptIn())
  const enabling = ref(false)
  const lastError = ref<string | null>(null)

  const canEnable = computed(
    () => supportStatus.value === 'supported' && permission.value !== 'denied',
  )
  const isActive = computed(
    () => optedIn.value && permission.value === 'granted',
  )

  // Initialize support status asynchronously
  getWebPushSupportStatus().then((status) => {
    supportStatus.value = status
  })

  async function refreshStatus() {
    supportStatus.value = await getWebPushSupportStatus()
    permission.value =
      typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
    optedIn.value = readWebPushOptIn()

    if (permission.value === 'granted') {
      const synced = await syncGrantedWebPushToken()
      if (synced) {
        optedIn.value = true
      }
    }
  }

  async function enable(): Promise<boolean> {
    lastError.value = null
    enabling.value = true
    try {
      await refreshStatus()
      if (!canEnable.value) {
        lastError.value =
          supportStatus.value === 'ios-needs-install'
            ? 'En iPhone o iPad, agrega Escribonauta a la pantalla de inicio para activar avisos push.'
            : supportStatus.value === 'missing-config'
              ? 'Los avisos push aún no están configurados en este entorno.'
              : 'Tu navegador no admite avisos push.'
        return false
      }

      const deviceToken = await subscribeFcmWebPush()
      permission.value =
        typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'

      if (!deviceToken) {
        lastError.value =
          permission.value === 'denied'
            ? 'Bloqueaste los avisos en el navegador. Puedes habilitarlos desde la configuración del sitio.'
            : 'No se pudo activar el permiso de notificaciones.'
        return false
      }

      await NotificationsService.registerWebPushDevice({ deviceToken })
      setLastRegisteredToken(deviceToken)
      writeWebPushOptIn(true)
      optedIn.value = true
      return true
    } catch (err) {
      lastError.value =
        err instanceof Error ? err.message : 'No se pudieron activar los avisos push'
      return false
    } finally {
      enabling.value = false
    }
  }

  return {
    supportStatus,
    permission,
    optedIn,
    enabling,
    lastError,
    canEnable,
    isActive,
    refreshStatus,
    enable,
  }
}
